import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Typography, Alert } from "antd";
import { ClockCircleOutlined, BellOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getDueSoonTasksApi, acknowledgeTaskReminderApi } from "../../apiservice/crm";
import { selectAuthInfo } from "../../pages/Auth/authSelectors";
// Same UTC -> institution-timezone conversion the CRM Tasks list itself uses
// (TodayTasks/index.js) — due_date comes back from the backend as a raw
// UTC datetime string with no "Z"/offset marker, so parsing it with a plain
// dayjs()/moment() call (as this component originally did) silently treats
// it as browser-local time instead of converting it, showing the wrong hour.
import { convertToCompanyTimezoneObject } from "../../lib/functions";
import { beepReminder } from "../../utils/beep";
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showBrowserNotification,
} from "../../utils/browserNotify";

const { Text, Paragraph } = Typography;

const POLL_INTERVAL_MS = 60000; // 1 minute — frequent enough to still catch
// the 10-minute window reliably without hammering the server.

const TYPE_LABELS = {
  call: "Call",
  followup: "Follow-up",
  manual: "Task",
  meeting: "Meeting",
  demo: "Demo",
  reminder: "Reminder",
};

// Always-mounted (via MainLayout, alongside RightSidebar/ChatAgent) so a CRM
// task reminder pops up 10 minutes before its due time regardless of which
// page the assigned user is currently on — not just while they're looking
// at the CRM Tasks tab. Stays on screen (no close icon, no click-outside,
// no Escape) until they click OK, which permanently dismisses it server-side
// (crm_tasks.reminder_shown_at) so it can't reappear after a refresh or in
// another open tab.
const CrmTaskReminder = () => {
  const authInfo = useSelector(selectAuthInfo);
  const userId = authInfo?.userInfo?.user_id;
  const [queue, setQueue] = useState([]);
  const [acknowledging, setAcknowledging] = useState(false);
  const seenTaskIds = useRef(new Set());

  // Whether to show the small "turn on desktop alerts" banner. Only shown
  // when permission hasn't been decided yet (not when denied — re-asking a
  // denied permission is a dead end in every browser) and the user hasn't
  // already dismissed it without deciding, remembered per-user like the
  // notification-panel "Clear all" state in RightSidebar.jsx.
  const bannerDismissedKey = `crm_reminder_alerts_prompt_dismissed_${userId || "user"}`;
  const [showEnableBanner, setShowEnableBanner] = useState(false);

  useEffect(() => {
    if (!authInfo || !isNotificationSupported()) return;
    const alreadyDismissed = localStorage.getItem(bannerDismissedKey) === "1";
    setShowEnableBanner(getNotificationPermission() === "default" && !alreadyDismissed);
  }, [authInfo, bannerDismissedKey]);

  const enableDesktopAlerts = async () => {
    const result = await requestNotificationPermission();
    setShowEnableBanner(false);
    if (result !== "default") {
      try { localStorage.setItem(bannerDismissedKey, "1"); } catch { /* storage unavailable */ }
    }
  };

  const dismissBanner = () => {
    setShowEnableBanner(false);
    try { localStorage.setItem(bannerDismissedKey, "1"); } catch { /* storage unavailable */ }
  };

  // Deliberately NOT gated by a "crm"/"sales" tab check — a task can be
  // (and, in practice, is) assigned to a user with no such role at all, and
  // that person still needs their reminder. The due-soon query is already
  // correctly scoped to the caller's own assigned_to and is index-backed, so
  // polling for every logged-in user costs one cheap row lookup per minute
  // even for someone who never has a CRM task.
  useEffect(() => {
    if (!authInfo) return undefined;

    const poll = async () => {
      try {
        const res = await getDueSoonTasksApi();
        const dueTasks = res?.data?.data || [];
        const fresh = dueTasks.filter((t) => !seenTaskIds.current.has(t.task_id));
        if (fresh.length === 0) return;
        fresh.forEach((t) => seenTaskIds.current.add(t.task_id));
        setQueue((prev) => [...prev, ...fresh]);

        // Browser-level notification + sound so this is noticeable even on
        // a different tab or a different application entirely — the
        // in-page Modal below only helps while this tab is the one in
        // focus. One chime per poll (not per task) keeps a batch of several
        // due tasks from sounding like a burst of alarms; each still gets
        // its own OS notification banner.
        beepReminder();
        fresh.forEach((t) => {
          const who = t.deal_title || t.customer_name || "a task";
          showBrowserNotification(`Reminder: ${TYPE_LABELS[t.type] || "Task"} due soon`, {
            body: `${who}${t.remarks ? ` — ${t.remarks}` : ""}`,
            tag: `crm-task-${t.task_id}`,
            icon: "/icon-192x192.png",
          });
        });
      } catch (err) {
        console.error("CRM task reminder poll failed:", err);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [authInfo]);

  const banner = showEnableBanner ? (
    <Alert
      type="info"
      showIcon
      icon={<BellOutlined />}
      closable
      onClose={dismissBanner}
      style={{
        position: "fixed",
        top: 12,
        right: 12,
        zIndex: 1050,
        maxWidth: 360,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
      }}
      message="Turn on desktop alerts for follow-up reminders"
      description={
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Get notified with a sound even when you're on a different tab or app.
          </Text>
          <div style={{ marginTop: 8 }}>
            <Button type="primary" size="small" onClick={enableDesktopAlerts}>
              Enable
            </Button>
          </div>
        </>
      }
    />
  ) : null;

  if (queue.length === 0) return banner;

  const current = queue[0];
  const title = current.deal_title || current.customer_name || "this task";
  const typeLabel = TYPE_LABELS[current.type] || "Task";
  const dueMoment = convertToCompanyTimezoneObject(current.due_date);
  // Date -> ISO string first so convertToCompanyTimezoneObject's own ISO
  // detection (dateStr.includes("T")) takes the moment.utc(...) branch
  // correctly, same as it does for the due_date string above.
  const nowMoment = convertToCompanyTimezoneObject(new Date().toISOString());
  const dueLabel = dueMoment.isSame(nowMoment, "day")
    ? `Today at ${dueMoment.format("h:mm A")}`
    : dueMoment.format("DD MMM YYYY, h:mm A");

  const handleOk = async () => {
    setAcknowledging(true);
    try {
      await acknowledgeTaskReminderApi(current.task_id);
    } catch (err) {
      console.error("Failed to acknowledge CRM task reminder:", err);
    } finally {
      setAcknowledging(false);
      setQueue((prev) => prev.slice(1));
    }
  };

  return (
    <>
      {banner}
      <Modal
        open
        closable={false}
        maskClosable={false}
        keyboard={false}
        title={
          <span>
            <ClockCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />
            Task Reminder
          </span>
        }
        footer={[
          <Button key="ok" type="primary" loading={acknowledging} onClick={handleOk}>
            OK
          </Button>,
        ]}
      >
        <Paragraph>
          <Text strong>{typeLabel}</Text> with <Text strong>{title}</Text> is due{" "}
          <Text strong>{dueLabel}</Text>.
        </Paragraph>
        {current.remarks && (
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {current.remarks}
          </Paragraph>
        )}
      </Modal>
    </>
  );
};

export default CrmTaskReminder;
